/* eslint-disable react/display-name */
import { Table } from 'grab-ui';
import React, { useMemo } from 'react';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import _some from 'lodash/some';
import { bulkUploadMetaDataSelector } from 'selectors/new-bulk-upload-selectors';
import { compulsoryCODSelector } from 'selectors/app-selector';
import { bookingConfigurationParcelSizesSelector } from 'selectors/new-booking-selectors';
import {
  Batch,
  Weight,
  CashOnDelivery,
  NameAndPhone,
  ServiceType,
  Address,
  VehicleType,
  Size,
} from './table-fields';
import styles from './check-upload-table.less';

const CheckUploadTable = ({
  form,
  rawData,
  setEditingAddress,
  rowIDsWithError,
  metaData,
  compulsoryCOD,
  parcelSizes,
}) => {
  const columns = useMemo(
    () => {
      const maxWeight = _get(metaData, 'meta.maxWeight', 20);
      const codMeta = _get(metaData, 'meta.cod');
      const showBatchCol = _some(
        rawData,
        row => _get(row, 'batch') && _get(row, 'sequenceNumber'),
      );
      const showServiceType = _some(rawData, 'serviceType');
      const showVehicleType = _some(rawData, 'vehicleType');

      return [
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_heading_batch',
          ),
          key: 'batch',
          render: (_, row) => <Batch row={row} rawData={rawData} />,
          hidden: !showBatchCol,
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_sender_happypath',
          ),
          key: 'sender',
          render: (_, row) => <NameAndPhone row={row} isPickUp form={form} />,
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_sender_address_happypath',
          ),
          key: 'senderAddress',
          render: (_, row) => (
            <Address
              row={row}
              isPickUp
              setEditingAddress={setEditingAddress}
              form={form}
              hasWidthExpanded={!showServiceType && !showVehicleType}
            />
          ),
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_recipient_happypath',
          ),
          key: 'recipient',
          render: (_, row) => (
            <NameAndPhone row={row} isPickUp={false} form={form} />
          ),
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_recipient_address_happypath',
          ),
          key: 'recipientAddress',
          render: (_, row) => (
            <Address
              row={row}
              isPickUp={false}
              setEditingAddress={setEditingAddress}
              form={form}
              hasWidthExpanded={!showServiceType && !showVehicleType}
            />
          ),
        },
        {
          title: 'Size',
          key: 'parcelSize',
          render: (_, row) => (
            <Size row={row} form={form} parcelSizes={parcelSizes} />
          ),
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_weight_happypath',
          ),
          key: 'weight',
          render: (_, row) => (
            <Weight row={row} form={form} maxWeight={maxWeight} />
          ),
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_cod_happypath',
          ),
          key: 'cod',
          width: '10%',
          render: (_, row) => (
            <CashOnDelivery
              row={row}
              form={form}
              codMeta={codMeta}
              compulsoryCOD={compulsoryCOD}
            />
          ),
          hidden: !codMeta,
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_delivery_type_happypath',
          ),
          key: 'serviceType',
          render: (_, row) => (
            <ServiceType row={row} form={form} metaData={metaData} />
          ),
          hidden: !showServiceType,
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_heading_vehicle_changing_vehicle_type',
          ),
          key: 'vehicleType',
          render: (_, row) => (
            <VehicleType row={row} form={form} metaData={metaData} />
          ),
          hidden: !showVehicleType,
        },
      ].filter(c => !c.hidden);
    },
    [form, rawData, setEditingAddress, metaData, compulsoryCOD],
  );

  const displayRow = useMemo(
    () => {
      if (rowIDsWithError) {
        return rawData.filter(row => rowIDsWithError[row.id]);
      }
      return rawData;
    },
    [rawData, rowIDsWithError],
  );

  return (
    <Table
      className={styles.checkUploadTable}
      rowClassName={styles.row}
      columns={columns}
      rowKey={'id'}
      pagination={false}
      dataSource={displayRow}
    />
  );
};

CheckUploadTable.defaultProps = {
  rowIDsWithError: undefined,
  compulsoryCOD: false,
};

CheckUploadTable.propTypes = {
  form: PropTypes.shape({}).isRequired,
  rowIDsWithError: PropTypes.shape({}),
  rawData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setEditingAddress: PropTypes.func.isRequired,
  metaData: PropTypes.shape({}).isRequired,
  compulsoryCOD: PropTypes.bool,
  parcelSizes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default connect(state => ({
  metaData: bulkUploadMetaDataSelector(state),
  parcelSizes: bookingConfigurationParcelSizesSelector(state),
  compulsoryCOD: compulsoryCODSelector(state),
}))(CheckUploadTable);
